import output from "_src/engine/output";
import workspace from "_src/workspace";
import appState from "_src/app_state";
import compat from "_src/engine/compat";
import windowManager from "_src/client/window_manager";
import {cameraType} from "_src/engine/constants";
import PhotoGroupWidget from "_src/client/widgets/photo_group";
import events from "_src/engine/events";
import controlFactory from "_src/client/controls/control_factory";
import {vec3} from "_src/libs/gl-matrix";
import inputCollector from "_src/client/input";
import {screenUtils} from "_src/client/utils";
import {photoState} from "_src/constants";
import renderGraph from "_src/render_graph/render_graph";
import PhotoControl from "_src/client/controls/photo_control";
import TrackballControl from "_src/client/controls/trackball";
import viewcube from "_src/client/viewcube";
import projectTree from "_src/reformer/project_tree";

const _widgets = new Map();
const _previewData = new Map();
let _currentPhotoViewGroupID = null;
let _currentPhotoViewPhotoID = null;
const _hoveredPhoto = {groupId: null, photoId: null};
let _selectedPhotos = [];
const _savedVisibility = new Map();

const PHOTO_MODE_START_ANIMATION_TIME = 0.5;
const PHOTO_MODE_END_ANIMATION_TIME = 0.5;

export default {
  init() {
    events.on("client_mousemove", _onMouseMove);
    events.on("client_mouseup",   _onMouseUp);
    events.on("control_set_target", _onDblClick);
    events.on("exitPhotoMode", _onPhotoModeExit);
    events.on("photoViewRectangleExternal", _onPhotoViewRectangle);
  },

  cleanup() {
    reset();
    events.off("client_mousemove", _onMouseMove);
    events.off("client_mouseup",   _onMouseUp);
    events.off("control_set_target", _onDblClick);
    events.off("exitPhotoMode", _onPhotoModeExit);
    events.off("photoViewRectangleExternal", _onPhotoViewRectangle);
  },

  reset,
  viewPhoto,
  update,

  setState(photoGroupID, photoID, state) {
    const isObjectAlreadyHovered = _hoveredPhoto.groupId === photoGroupID && _hoveredPhoto.photoId === photoID;
    const isObjectAlreadySelected = _selectedPhotos.find(selectedPhoto => photoGroupID === selectedPhoto.groupId && photoID === selectedPhoto.photoId);
    const isObjectInPhotoMode = _currentPhotoViewGroupID === photoGroupID && _currentPhotoViewPhotoID === photoID;

    switch (state) {
      case photoState.ACTIVE: {
        if (isObjectAlreadySelected) return;
        if (isObjectAlreadyHovered) {
          _hoveredPhoto.groupId = null;
          _hoveredPhoto.photoId = null;
        }

        _selectedPhotos.push({groupId: photoGroupID, photoId: photoID});

        if (isObjectInPhotoMode) return;
        break;
      }

      case photoState.HOVERED: {
        if (isObjectAlreadySelected) {
          _selectedPhotos = _selectedPhotos.filter(selectedPhoto => selectedPhoto.groupId !== photoGroupID || selectedPhoto.photoId !== photoID);
        }
        if (isObjectAlreadyHovered) return;
        _hoveredPhoto.groupId = photoGroupID;
        _hoveredPhoto.photoId = photoID;

        if (isObjectInPhotoMode) return;
        break;
      }

      case photoState.DEFAULT: {
        if (isObjectAlreadySelected) {
          _selectedPhotos = _selectedPhotos.filter(selectedPhoto => selectedPhoto.groupId !== photoGroupID || selectedPhoto.photoId !== photoID);
        } else if (isObjectAlreadyHovered) {
          _hoveredPhoto.groupId = null;
          _hoveredPhoto.photoId = null;
        } else {
          return;
        }

        break;
      }
    }

    _setStateInternal(photoGroupID, photoID, state);
  },

  setTransparentModeEnabled(enabled) {
    if (_currentPhotoViewGroupID === null) return;

    const widget = _widgets.get(_currentPhotoViewGroupID);
    widget.setTransparentModeEnabled(enabled);
    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
  },

  updateFitArea() {
    if (_currentPhotoViewGroupID === null) return;

    _resizePhotoView(_currentPhotoViewGroupID);
  },
}

function _onPhotoViewRectangle(rect) {
  const payload = rect ? {
    rect: rect,
    photoGroupId: _currentPhotoViewGroupID,
    photoId: _currentPhotoViewPhotoID,
  } : null;
  events.emit("photoViewRectangle", payload);
}

function _onFileProgress(objectID, photoID, progress) {
  if (_currentPhotoViewGroupID === objectID && _currentPhotoViewPhotoID === photoID) {
    events.emit("photoProgress", progress);
  }
}

const _onMouseMove = (function () {
  const vec3tmp = vec3.create();
  const vec3tmp2 = vec3.create();
  const vec3tmp3 = vec3.create();

  return function () {
    if (_currentPhotoViewGroupID !== null || _widgets.size === 0) return;

    const coords = inputCollector.getCoords();
    const screenX = coords[0];
    const screenY = coords[1];

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.cameraControl.object;
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, screenX, screenY);

    const origin = camera.calculateOrigin(x, y, vec3tmp);
    const ray = camera.calculateRay(x, y, vec3tmp2);
    const point = vec3tmp3;

    let closestDistance = Infinity;
    let closestGroupId = null;
    let closestWidgetId = null;
    for (const [groupId, widget] of _widgets.entries()) {
      const [widgetId, distance] = widget.castRay(origin, ray, point);
      if (distance < closestDistance) {
        closestGroupId = groupId;
        closestWidgetId = widgetId;
        closestDistance = distance;
      }
    }

    if (_hoveredPhoto.groupId !== closestGroupId || _hoveredPhoto.photoId !== closestWidgetId) {
      if (_hoveredPhoto.groupId !== null && !_selectedPhotos.find(selectedPhoto => _hoveredPhoto.groupId === selectedPhoto.groupId && _hoveredPhoto.photoId === selectedPhoto.photoId)) {
        _setStateInternal(_hoveredPhoto.groupId, _hoveredPhoto.photoId, photoState.DEFAULT);
      }

      if (closestGroupId !== null) {
        if (!_selectedPhotos.find(selectedPhoto => closestGroupId === selectedPhoto.groupId && closestWidgetId === selectedPhoto.photoId)) {
          _setStateInternal(closestGroupId, closestWidgetId, photoState.HOVERED);
        }
        _hoveredPhoto.groupId = closestGroupId;
        _hoveredPhoto.photoId = closestWidgetId;
      } else {
        _hoveredPhoto.groupId = null;
        _hoveredPhoto.photoId = null;
      }
    }

  }
})()

function _onMouseUp (e) {
  if (e.button !== 0 || _currentPhotoViewGroupID !== null) {
    return;
  }

  if (_hoveredPhoto.groupId !== null) {
    events.emit("photoClicked");
  }

  if (!_selectedPhotos.find(selectedPhoto => _hoveredPhoto.groupId === selectedPhoto.groupId && _hoveredPhoto.photoId === selectedPhoto.photoId)) {
    for (const selectedPhoto of _selectedPhotos) {
      _setStateInternal(selectedPhoto.groupId, selectedPhoto.photoId, photoState.DEFAULT);
    }
    _selectedPhotos.length = 0;

    if (_hoveredPhoto.groupId !== null) {
      _selectedPhotos.push({groupId: _hoveredPhoto.groupId, photoId: _hoveredPhoto.photoId});
      _setStateInternal(_hoveredPhoto.groupId, _hoveredPhoto.photoId, photoState.ACTIVE);
    }
  }
}

function _onDblClick () {
  if (_currentPhotoViewGroupID !== null) return;

  if (_hoveredPhoto.groupId !== null) {
    for (const selectedPhoto of _selectedPhotos) {
      _setStateInternal(selectedPhoto.groupId, selectedPhoto.photoId, photoState.DEFAULT);
    }
    _selectedPhotos.length = 0;

    viewPhoto(_hoveredPhoto.groupId, _hoveredPhoto.photoId);
    _hoveredPhoto.groupId = null;
    _hoveredPhoto.photoId = null;
  }
}

function _onPhotoModeExit(rotationState) {
  _exitPhotoMode(_currentPhotoViewGroupID, PHOTO_MODE_END_ANIMATION_TIME / 2, rotationState);
  _updateVisibility();
}

function _resizePhotoView(photoGroupID) {
  const widget = _widgets.get(photoGroupID);
  if (!widget) {
    output.error(output.DEVELOPERS_ERROR, "Can not resize photo view mode. Photo group with id " + photoGroupID + " is not exist or not initialized");
    return;
  }

  _animationFinishHack();
  widget.resizePhotoMode(windowManager.getWindow(windowManager.MAIN).cameraControl);
}

function _animationFinishHack() {
  const control = windowManager.getWindow(windowManager.MAIN).cameraControl;
  control.update(999999);
}

function _exitPhotoMode(photoGroupID, animationTime, rotationState = null) {
  const widget = _widgets.get(photoGroupID);
  if (!widget) {
    output.error(output.DEVELOPERS_ERROR, "Can not exit photo view mode. Photo group with id " + photoGroupID + " is not exist or not initialized");
    return;
  }

  if (_currentPhotoViewGroupID === null) {
    return;
  }

  for (const photoGroup of workspace.getPhotoGroups()) {
    if (_savedVisibility.has(photoGroup.getID())) {
      photoGroup.setVisibility(_savedVisibility.get(photoGroup.getID()));
    }
  }
  _savedVisibility.clear();

  const mainWindow = windowManager.getWindow(windowManager.MAIN);
  const photoViewCameraControl = mainWindow.cameraControl;

  const onFinish = () => {
    appState.setStateValue("cameraControl", appState.CC_TRACKBALL);
    _updateWindowControl();
    mainWindow.cameraControl.setPosition(photoViewCameraControl.object.trans);
    if (rotationState !== null) mainWindow.cameraControl.startRotation(...rotationState);
    _currentPhotoViewGroupID = null;
    _currentPhotoViewPhotoID = null;
    viewcube.setEnabled(appState.getStateValue("showViewcube"));
  }

  photoViewCameraControl.moveOutOfPhoto(animationTime, onFinish);
  widget.exitPhotoMode();

  if (!_selectedPhotos.find(selectedPhoto => _currentPhotoViewGroupID === selectedPhoto.groupId && _currentPhotoViewPhotoID === selectedPhoto.photoId)) {
    _setStateInternal(_currentPhotoViewGroupID, _currentPhotoViewPhotoID, photoState.DEFAULT);
  }
}

async function _initUndistortion(photoGroupID) {
  if (!compat.isWebGL2) return;

  const widget = _widgets.get(photoGroupID);
  if (!widget) {
    output.error(output.API_ERROR, "Can not load undistortion map. Photo group with id " + photoGroupID + " is not exist or not initialized");
    return;
  }

  const undistortion = await workspace.getPhotoGroupByID(photoGroupID).initUndistortion();
  if (undistortion) widget.updateUndistortion(undistortion);
}

async function _enterPhotoMode(photoGroupID, photoID) {
  const widget = _widgets.get(photoGroupID);
  if (!widget) {
    output.error(output.API_ERROR, "Can not view photo. Photo group with id " + photoGroupID + " is not exist or not initialized");
    return;
  }

  const isFirstTime = _currentPhotoViewGroupID === null;
  if (isFirstTime) {
    for (const photoGroup of workspace.getPhotoGroups()) {
      _savedVisibility.set(photoGroup.getID(), photoGroup.getVisibility());
      photoGroup.setVisibility(false);
    }
    viewcube.setEnabled(false);
  }

  const mainWindow = windowManager.getWindow(windowManager.MAIN);
  let photoControl = mainWindow.cameraControl;
  if (!(photoControl instanceof PhotoControl)) {
    photoControl = controlFactory.createControl(appState.CC_PHOTO, photoControl.object);
  }

  if (!_selectedPhotos.find(selectedPhoto => _currentPhotoViewGroupID === selectedPhoto.groupId && _currentPhotoViewPhotoID === selectedPhoto.photoId)) {
    _setStateInternal(_currentPhotoViewGroupID, _currentPhotoViewPhotoID, photoState.DEFAULT);
  }

  if (_hoveredPhoto.groupId !== null) {
    _setStateInternal(_hoveredPhoto.groupId, _hoveredPhoto.photoId, photoState.DEFAULT);
  }
  _hoveredPhoto.groupId = null;
  _hoveredPhoto.photoId = null;

  _currentPhotoViewGroupID = photoGroupID;
  _currentPhotoViewPhotoID = photoID;
  _setStateInternal(_currentPhotoViewGroupID, _currentPhotoViewPhotoID, photoState.ACTIVE);

  let availableAnimationTime = PHOTO_MODE_START_ANIMATION_TIME;
  if (photoControl.object.type !== cameraType.PERSPECTIVE) {
    const changeProjectionAnimationPromise = new Promise(resolve => {
      const rg = mainWindow.renderGraph;
      const gridCamera = rg.getPass(renderGraph.GRID).getSecondScene().camera;
      const changeProjectionTime = availableAnimationTime / 3;
      availableAnimationTime -= changeProjectionTime;
      mainWindow.cameraControl.setProjectionType(cameraType.PERSPECTIVE, gridCamera,
          () => {
            appState.setStateValue("cameraType", cameraType.PERSPECTIVE);
          },
          () => {
            rg.requestFrame();
          },
          () => {
            rg.requestFrame();
            resolve();
          },
          changeProjectionTime);
    });

    await changeProjectionAnimationPromise;
  }

  mainWindow.setCameraControl(photoControl);
  photoControl.setEnabled(false);
  widget.enterPhotoMode(photoControl, photoID, availableAnimationTime, isFirstTime);

  const texture = await workspace.getPhotoGroupByID(photoGroupID).initPhoto(photoID, _onFileProgress);
  if (texture) {
    widget.updateImage(photoID, texture);
    photoControl.setEnabled(true);

    mainWindow.renderGraph.requestFrame();
  }
}

function _addPhotoGroupToScene(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;

  const scene = graph.getPhotosScene();
  widget.addToScene(scene);

  graph.requestFrame();
}

function _removePhotoGroupFromScene(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;

  const scene = graph.getPhotosScene();
  widget.removeFromScene(scene);

  graph.requestFrame();
}

function _updateVisibility() {
  for (const [photoGroupID, photoGroupWidget] of _widgets.entries()) {
    const photoGroup = workspace.getPhotoGroupByID(photoGroupID);
    if (!photoGroup || !photoGroup.loaded || photoGroup.getHash() !== photoGroupWidget.hash) {
      if (_currentPhotoViewGroupID === photoGroupID) {
        _exitPhotoMode(_currentPhotoViewGroupID, PHOTO_MODE_END_ANIMATION_TIME);
      }

      _removePhotoGroupFromScene(photoGroupWidget);
      _widgets.delete(photoGroupID);
    }
  }

  for (const photoGroup of workspace.getPhotoGroups()) {
    const photoGroupID = photoGroup.getID();
    if (!_widgets.has(photoGroupID)) {
      if (!photoGroup.getVisibility() || !photoGroup.loaded) {
        //Don't create widget until it becomes visible
        continue;
      }

      const photoGroupWidget = new PhotoGroupWidget(photoGroup);
      _widgets.set(photoGroupID, photoGroupWidget);
      _addPhotoGroupToScene(photoGroupWidget);
    }

    const photoGroupWidget = _widgets.get(photoGroupID);
    photoGroupWidget.updateVisibility(photoGroup.getVisibility());
  }

  events.emit("object_visibility_change");
  windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
}

function _updateState() {
  for (const photoGroup of workspace.getPhotoGroups()) {
    const photoGroupID = photoGroup.getID();
    const widget = _widgets.get(photoGroupID);
    if (!widget) {
      continue;
    }

    widget.updateState(photoGroup);
  }
  windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
}

function _setStateInternal(photoGroupID, photoID, state) {
  if (photoGroupID === null) return;

  projectTree.setPhotoState(photoGroupID, photoID, state);
  _updateState();
}

function viewPhoto(photoGroupID, photoID, instantly = false) {
  if (photoGroupID === null) {
    _exitPhotoMode(_currentPhotoViewGroupID, PHOTO_MODE_END_ANIMATION_TIME);
  } else {
    _enterPhotoMode(photoGroupID, photoID, PHOTO_MODE_START_ANIMATION_TIME);
    _initUndistortion(photoGroupID);
  }

  if (instantly) _animationFinishHack();
  _updateVisibility();
  windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
}

function reset() {
  for (const widget of _widgets.values()) {
    _removePhotoGroupFromScene(widget);
  }
  _widgets.clear();
  _previewData.clear();
  _updateWindowControl();
}

function update() {
  _updateVisibility();
  _updateState();
}

function _updateWindowControl() {
  const mainWindow = windowManager.getWindow(windowManager.MAIN);
  const currentControl = mainWindow.cameraControl;
  if (!(currentControl instanceof TrackballControl)) {
    const newControl = controlFactory.createControl(appState.CC_TRACKBALL, currentControl.object);
    mainWindow.setCameraControl(newControl);
  }
}
